import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { ActionIcon, Divider, Group, Textarea, Tooltip } from '@mantine/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../../../ui-library/button/button';
import { Text } from '../../../../ui-library/typography/typography';
import type { AskUserQuestion } from '../../flora.type';
import { useAgentMessageListScroll } from './agent-message-list-scroll.context';
import { AgentPromptRejection } from './agent-prompt-rejection';

interface AgentQuestionCardProps {
  questions: AskUserQuestion[];
  onSubmit: (answers: Record<string, string | string[]>) => void;
  disabled?: boolean;
  submittedAnswers?: Record<string, string | string[]>;
  /** Why the backend refused the last submit; shown while the card is answerable again. */
  rejection?: string;
}

export function AgentQuestionCard({
  questions,
  onSubmit,
  disabled = false,
  submittedAnswers,
  rejection,
}: AgentQuestionCardProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string | string[]>>({});
  const [customInputs, setCustomInputs] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const listScroll = useAgentMessageListScroll();

  const currentQuestion = questions[activeTab];
  const totalTabs = questions.length;

  // Align to top instead of nearest when the card is taller than the scroll
  // viewport, so the question and options stay readable instead of being
  // pushed off-screen.
  useEffect(() => {
    const card = cardRef.current;
    if (!expanded || !card?.scrollIntoView) return;
    // The list's stick-to-bottom follow would snap straight back past the card
    // as it grows; the user asked to read this card, so stop following.
    listScroll?.releaseStickToBottom();
    const viewport = getScrollParent(card);
    const fits = !viewport || card.offsetHeight <= viewport.clientHeight;
    card.scrollIntoView({ behavior: 'smooth', block: fits ? 'nearest' : 'start' });
  }, [expanded, listScroll]);

  const handleOptionClick = useCallback(
    (question: string, label: string, multiSelect: boolean) => {
      if (disabled) return;

      setSelectedAnswers((prev) => {
        if (multiSelect) {
          const existingValue = prev[question];
          const current = Array.isArray(existingValue)
            ? existingValue
            : typeof existingValue === 'string'
              ? [existingValue]
              : [];
          const isSelected = current.includes(label);
          const updated = isSelected ? current.filter((item) => item !== label) : [...current, label];
          return { ...prev, [question]: updated };
        }
        setCustomInputs((ci) => ({ ...ci, [question]: '' }));
        return { ...prev, [question]: label };
      });

      // Auto-advance to next tab on single-select
      if (!multiSelect && activeTab < totalTabs - 1) {
        setTimeout(() => setActiveTab((t) => t + 1), 150);
      }
    },
    [disabled, activeTab, totalTabs],
  );

  const handleCustomInput = useCallback(
    (question: string, value: string) => {
      if (disabled) return;
      setCustomInputs((prev) => ({ ...prev, [question]: value }));
      if (value.trim()) {
        setSelectedAnswers((prev) => ({ ...prev, [question]: value.trim() }));
      } else {
        setSelectedAnswers((prev) => {
          const current = prev[question];
          if (
            typeof current === 'string' &&
            !questions.find((q) => q.question === question)?.options.some((o) => o.label === current)
          ) {
            const { [question]: _, ...rest } = prev;
            return rest;
          }
          return prev;
        });
      }
    },
    [disabled, questions],
  );

  // Use submittedAnswers for historical view, selectedAnswers for active
  const effectiveAnswers = useMemo(
    () => (disabled && submittedAnswers ? submittedAnswers : selectedAnswers),
    [disabled, submittedAnswers, selectedAnswers],
  );

  const isOptionSelected = useCallback(
    (question: string, label: string): boolean => {
      const answer = effectiveAnswers[question];
      if (Array.isArray(answer)) return answer.includes(label);
      return answer === label;
    },
    [effectiveAnswers],
  );

  const isQuestionAnswered = useCallback(
    (question: AskUserQuestion): boolean => {
      const answer = effectiveAnswers[question.question];
      if (!answer) return false;
      if (Array.isArray(answer)) return answer.length > 0;
      return true;
    },
    [effectiveAnswers],
  );

  /** Returns the custom text answer if it doesn't match any option label */
  const getCustomAnswer = useCallback(
    (question: AskUserQuestion): string | null => {
      const answer = effectiveAnswers[question.question];
      if (!answer || Array.isArray(answer)) return null;
      const isKnownOption = question.options.some((o) => o.label === answer);
      return isKnownOption ? null : answer;
    },
    [effectiveAnswers],
  );

  const allQuestionsAnswered = useMemo(() => questions.every(isQuestionAnswered), [questions, isQuestionAnswered]);

  const goNext = useCallback(() => {
    if (activeTab < totalTabs - 1) setActiveTab((t) => t + 1);
  }, [activeTab, totalTabs]);

  const goPrev = useCallback(() => {
    if (activeTab > 0) setActiveTab((t) => t - 1);
  }, [activeTab]);

  const handleSubmit = useCallback(() => {
    if (allQuestionsAnswered && !disabled) {
      onSubmit(selectedAnswers);
    }
  }, [allQuestionsAnswered, disabled, onSubmit, selectedAnswers]);

  const isLastTab = activeTab === totalTabs - 1;
  const isFirstTab = activeTab === 0;

  if (questions.length === 0) return null;

  if (disabled && !expanded) {
    return (
      <CollapsedWrap type="button" onClick={() => setExpanded(true)} aria-expanded={false}>
        <Icon icon="material-symbols:help-outline" width={14} height={14} />
        <Text size="small" weight="medium">
          Flora asked {totalTabs} {totalTabs === 1 ? 'question' : 'questions'}
        </Text>
        <Icon icon="material-symbols:expand-more" width={16} height={16} />
      </CollapsedWrap>
    );
  }

  return (
    <CardWrap ref={cardRef} data-disabled={disabled}>
      {totalTabs > 1 && (
        <TabBar>
          <Group justify="space-between">
            {questions.map((q, i) => (
              <TabPill
                key={i}
                type="button"
                data-active={i === activeTab}
                data-answered={isQuestionAnswered(q)}
                onClick={() => setActiveTab(i)}
              >
                {q.header ? q.header : i + 1}
              </TabPill>
            ))}
          </Group>
          <Group gap={4}>
            <Text size="tiny" color="var(--ink-lightest)">
              {activeTab + 1} / {totalTabs}
            </Text>
            {disabled && (
              <Button variant="subtle" size="xs" onClick={() => setExpanded(false)}>
                <Group gap={4}>
                  <Icon icon="material-symbols:expand-less" width={16} height={16} />
                  Collapse
                </Group>
              </Button>
            )}
          </Group>
        </TabBar>
      )}

      {currentQuestion && (
        <QuestionPane>
          <Text size="regular" weight="medium">
            {currentQuestion.question}
          </Text>

          <OptionsGrid>
            {currentQuestion.options.map((option, i) => (
              <Tooltip key={i} label={option.description} disabled={!option.description} position="top-start" multiline>
                <OptionCard
                  type="button"
                  data-selected={isOptionSelected(currentQuestion.question, option.label)}
                  disabled={disabled}
                  onClick={() =>
                    handleOptionClick(currentQuestion.question, option.label, currentQuestion.multi_select)
                  }
                >
                  <Text size="small" weight="medium">
                    {option.label}
                  </Text>
                  {option.description && (
                    <Text size="tiny" color="var(--ink-light)">
                      {option.description}
                    </Text>
                  )}
                </OptionCard>
              </Tooltip>
            ))}
          </OptionsGrid>

          {(() => {
            const customAnswer = getCustomAnswer(currentQuestion);
            if (disabled && customAnswer) {
              return (
                <>
                  <Divider />
                  <CustomAnswerDisplay>
                    <Text
                      size="tiny"
                      weight="medium"
                      color="var(--ink-light)"
                      style={{ textTransform: 'uppercase', letterSpacing: '0.3px' }}
                    >
                      Custom answer
                    </Text>
                    <CustomAnswerText>{customAnswer}</CustomAnswerText>
                  </CustomAnswerDisplay>
                </>
              );
            }
            if (!disabled) {
              return (
                <Textarea
                  label="Or provide custom instructions"
                  placeholder="Type your own answer..."
                  value={customInputs[currentQuestion.question] || ''}
                  onChange={(e) => handleCustomInput(currentQuestion.question, e.target.value)}
                  disabled={disabled}
                  minRows={2}
                  autosize
                  styles={{
                    label: {
                      fontSize: 'var(--font-size-xsmall)',
                      fontWeight: 500,
                      color: 'var(--ink-light)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      marginBottom: 4,
                    },
                  }}
                />
              );
            }
            return null;
          })()}
        </QuestionPane>
      )}

      <FooterRow>
        <Group gap="xs">
          {!isFirstTab && (
            <ActionIcon
              variant="subtle"
              size="md"
              onClick={goPrev}
              disabled={isFirstTab || disabled}
              aria-label="Previous question"
            >
              <Icon icon="material-symbols:chevron-left" width={18} height={18} />
            </ActionIcon>
          )}
          {!isLastTab && (
            <ActionIcon
              variant="subtle"
              size="md"
              onClick={goNext}
              disabled={isLastTab || disabled}
              aria-label="Next question"
            >
              <Icon icon="material-symbols:chevron-right" width={18} height={18} />
            </ActionIcon>
          )}
        </Group>
        <Group gap="xs">
          {!disabled && isLastTab && (
            <Button variant="secondary" size="sm" onClick={handleSubmit} disabled={!allQuestionsAnswered}>
              Submit
            </Button>
          )}
          {!disabled && !isLastTab && (
            <Button variant="secondary" size="sm" onClick={goNext} disabled={disabled}>
              <Group gap={4}>
                Next
                <Icon icon="material-symbols:chevron-right" width={16} height={16} />
              </Group>
            </Button>
          )}
        </Group>
      </FooterRow>
      {!disabled && rejection && <AgentPromptRejection message={rejection} />}
    </CardWrap>
  );
}

/**
 * Finds the nearest scrollable ancestor of an element.
 *
 * @param {HTMLElement} node - The element to search upward from
 * @returns {HTMLElement | null} The nearest ancestor with a scrollable overflow, or null if none
 */
function getScrollParent(node: HTMLElement): HTMLElement | null {
  let el: HTMLElement | null = node.parentElement;
  while (el) {
    const overflowY = getComputedStyle(el).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return el;
    el = el.parentElement;
  }
  return null;
}

// -- Minimal styled wrappers (layout only, no button/text styling) --

const CardWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  margin: 12px 0;
  /* Clear the sticky chat header when expand auto-scroll top-aligns a tall card. */
  scroll-margin-top: 56px;
  border: 1px solid var(--sky-light);
  border-radius: 12px;
  background: var(--sky-lightest);

  &[data-disabled='true'] {
    opacity: 0.7;
  }

  [data-theme='dark'] & {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.1);
  }
`;

const TabBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--sky-light);

  [data-theme='dark'] & {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }
`;

const TabPill = styled.button`
  padding: 3px 10px;
  border: 1px solid var(--sky-base);
  border-radius: 12px;
  background: var(--base-white);
  color: var(--ink-light);
  font-size: var(--font-size-xsmall);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  line-height: 1.3;

  &[data-active='true'] {
    border-color: var(--secondary-base);
    background: var(--secondary-lightest);
    color: var(--secondary-dark);
  }

  &[data-answered='true']:not([data-active='true']) {
    border-color: var(--success-base, #22c55e);
    color: var(--success-dark, #16a34a);
  }

  [data-theme='dark'] & {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.12);
    color: #a0a0a0;

    &[data-active='true'] {
      background: rgba(var(--secondary-base-rgb, 99, 102, 241), 0.2);
      border-color: var(--secondary-base);
      color: var(--secondary-light);
    }

    &[data-answered='true']:not([data-active='true']) {
      border-color: var(--success-base, #22c55e);
      color: var(--success-base, #22c55e);
    }
  }
`;

const QuestionPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const OptionCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 14px;
  border: 1px solid var(--sky-base);
  border-radius: 8px;
  background: var(--base-white);
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  width: 100%;

  &:hover:not(:disabled) {
    border-color: var(--secondary-light);
    background: var(--secondary-lightest);
  }

  &[data-selected='true'] {
    border-color: var(--secondary-base);
    background: var(--secondary-lightest);
    box-shadow: inset 0 0 0 1px var(--secondary-base);
  }

  &:disabled {
    cursor: default;
  }

  [data-theme='dark'] & {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.1);

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.08);
      border-color: var(--secondary-light);
    }

    &[data-selected='true'] {
      background: rgba(var(--secondary-base-rgb, 99, 102, 241), 0.15);
      border-color: var(--secondary-base);
      box-shadow: inset 0 0 0 1px var(--secondary-base);
    }
  }
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 8px;
  border-top: 1px solid var(--sky-light);

  [data-theme='dark'] & {
    border-top-color: rgba(255, 255, 255, 0.08);
  }
`;

const CustomAnswerDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 14px;
  border: 1px solid var(--secondary-base);
  border-radius: 8px;
  background: var(--secondary-lightest);
  box-shadow: inset 0 0 0 1px var(--secondary-base);

  [data-theme='dark'] & {
    background: rgba(var(--secondary-base-rgb, 99, 102, 241), 0.15);
    border-color: var(--secondary-base);
  }
`;

const CustomAnswerText = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: var(--secondary-dark);
  line-height: 1.5;
  white-space: pre-wrap;

  [data-theme='dark'] & {
    color: var(--secondary-light);
  }
`;

const CollapsedWrap = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  margin: 8px 0;
  border: 1px solid var(--sky-light);
  border-radius: 8px;
  background: var(--sky-lightest);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  width: fit-content;
  color: var(--ink-light);
  font: inherit;

  &:hover {
    background: var(--sky-lighter);
    border-color: var(--sky-base);
  }

  [data-theme='dark'] & {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.1);
    color: #a0a0a0;

    &:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.15);
    }
  }
`;
